<template>
  <a-card :title="$t('product.import_page.title')" :bordered="false">
    <a-radio-group
      v-model="isVariant"
      button-style="solid"
      class="product-variant mb-4"
      :disabled="downloadLoading || uploadLoading"
      @change="changeVariant"
    >
      <a-radio-button value="non-variant">
        {{ $t('product.import_page.non_variant') }}
      </a-radio-button>
      <a-radio-button value="multi-variant">
        {{ $t('product.import_page.multi_variant') }}
      </a-radio-button>
      <a-radio-button value="create-bundling">
        {{ $t('product.import_page.create_bundling') }}
      </a-radio-button>
    </a-radio-group>
    <p class="mt-4">
      {{ $t('product.import_page.easy_way_desc') }}
    </p>
    <a-card style="width: 75%">
      <div class="mb-4">
        <a-row :gutter="16">
          <a-col :span="16">
            <h6 style="font-weight: bold">
              {{ $t('product.import_page.download_template_title') }}
            </h6>
            <p class="mb-0">
              {{ $t('product.import_page.download_template_desc') }}
            </p>
          </a-col>
          <a-col :span="8">
            <a-button
              type="primary"
              class="mt-4"
              style="width: 200px"
              :loading="downloadLoading"
              :disabled="uploadLoading"
              @click="downloadTemplate"
            >
              <a-icon v-if="!downloadLoading" type="download" />
              {{ $t('product.import_page.download_template_btn') }}
            </a-button>
          </a-col>
        </a-row>
      </div>
      <hr />
      <div class="mt-4">
        <a-row :gutter="16">
          <a-col :span="16">
            <h6 style="font-weight: bold">
              {{ $t('product.import_page.upload_template_title') }}
            </h6>
            <p class="mb-0">
              {{ $t('product.import_page.upload_template_desc') }}
            </p>
          </a-col>
          <a-col :span="8">
            <a-button
              v-if="fileList.length"
              type="primary"
              class="mt-4"
              style="width: 200px"
              :loading="uploadLoading"
              :disabled="downloadLoading"
              @click="uploadTemplate"
            >
              <a-icon v-if="!uploadLoading" type="upload" />
              {{ $t('product.import_page.upload_template_btn') }}
            </a-button>
            <a-upload
              :file-list="fileList"
              :remove="handleRemove"
              :before-upload="beforeUpload"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            >
              <a-button
                v-if="!fileList.length"
                type="primary"
                class="mt-4"
                style="width: 200px; background-color: #fff !important; color: #E00000 !important"
                ghost
              >
                <a-icon type="upload" />
                {{ $t('product.import_page.select_file') }}
              </a-button>
            </a-upload>
          </a-col>
        </a-row>
      </div>
    </a-card>

    <!-- Active Processes Section -->
    <div v-if="isVariant === 'create-bundling' && (loadingHistory || activeProcesses.length > 0 || historyProcesses.length > 0)" class="mt-4">
      <div class="flex justify-between items-center mb-3" style="width: 75%">
        <h6 style="font-weight: bold" class="mb-0">
          {{ activeProcesses.length > 0 ? $t('product.import_page.active_processes_title') : $t('product.import_page.process_history_title') }}
        </h6>
        <a-button 
          v-if="hasCompletedSessionProcesses"
          type="link" 
          size="small" 
          style="color: #595959; font-weight: 600;"
          @click="clearCompletedProcesses"
        >
          {{ $t('product.import_page.clear_completed') }} <a-icon type="check-circle" />
        </a-button>
        <a-button 
          v-else-if="historyProcesses.length > 0"
          type="link" 
          size="small" 
          style="color: #1890ff; font-weight: 600;"
          @click="scrollToHistory"
        >
          {{ $t('product.import_page.process_history_title') }} <a-icon type="history" />
        </a-button>
      </div>

      <a-card v-for="process in activeProcesses" :key="process.request_id" class="mb-4 active-process-card-v2" :body-style="{ padding: '16px 24px' }">
        <div class="process-header">
          <div class="header-left">
            <span class="file-name">{{ process.file_name || 'Processing file...' }}</span>
            <div class="status-pill" :class="process.status.toLowerCase()">
              <span class="dot" />
              {{ process.status === 'ON_PROGRESS' ? $t('product.import_page.in_progress') : (process.status === 'FINISHED' || process.status === 'COMPLETED' ? $t('product.import_page.success_label') : process.status) }}
            </div>
          </div>
          <div class="header-right">
            <a-popconfirm
              v-if="process.status === 'ON_PROGRESS' || process.status === 'PENDING'"
              title="Stop this process?"
              ok-text="Stop"
              cancel-text="Keep"
              ok-type="danger"
              @confirm="cancelProcess(process.request_id)"
            >
              <a-button class="cancel-btn">
                <a-icon type="stop" /> {{ $t('product.import_page.cancel_process') }}
              </a-button>
            </a-popconfirm>
            <a-button v-else-if="['FAILED', 'FINISHED', 'COMPLETED'].includes(process.status)" type="primary" class="detail-btn" @click="viewDetail(process)">
              <a-icon type="eye" /> {{ $t('product.import_page.view_details') }}
            </a-button>
          </div>
        </div>

        <div class="process-body">
          <div v-if="process.status !== 'FAILED' && (process.items_total || 0) > 0" class="progress-bar-wrapper">
            <div 
              class="custom-progress-bar" 
              :class="process.status.toLowerCase()"
              :style="{ width: Math.round((( (process.items_completed || 0) + (process.items_failed || 0) + (process.items_skipped || 0) ) / (process.items_total || 1)) * 100) + '%' }"
            >
              <span class="inner-percentage">{{ Math.round((( (process.items_completed || 0) + (process.items_failed || 0) + (process.items_skipped || 0) ) / (process.items_total || 1)) * 100) }}%</span>
            </div>
          </div>
          
          <div v-else-if="process.status === 'ON_PROGRESS'" class="validation-loading-banner mb-3">
            <a-spin size="small" class="mr-3" />
            <span class="loading-text">{{ $t('product.import_page.validating_excel') }}</span>
          </div>
          
          <div v-if="process.status === 'FAILED' && process.message" class="validation-error-banner mt-3 mb-3">
            <a-icon type="warning" theme="filled" class="mr-2" />
            <span class="error-text">{{ process.message }}</span>
          </div>

          <!-- Session Result Summary (e.g. Success/Fail counts for one-off local view) -->
          <div v-if="sessionRequestIds.includes(process.request_id) && process.status !== 'ON_PROGRESS' && process.status !== 'PENDING'" class="session-result-summary mb-3">
            <div class="summary-item success">
              <span class="dot" />
              <span class="label">{{ $t('product.import_page.success_label') }}:</span>
              <span class="value">{{ process.items_completed || 0 }}</span>
            </div>
            <div class="summary-item failed">
              <span class="dot" />
              <span class="label">{{ $t('product.import_page.failed_label') }}:</span>
              <span class="value">{{ process.items_failed || 0 }}</span>
            </div>
            <div class="summary-item skipped">
              <span class="dot" />
              <span class="label">{{ $t('product.import_page.skipped_label') }}:</span>
              <span class="value">{{ process.items_skipped || 0 }}</span>
            </div>
          </div>
          
          <div class="process-footer">
            <div class="footer-left">
              <span class="started-at">
                <a-icon type="clock-circle" style="font-size: 12px; margin-right: 4px;" />
                {{ $t('product.import_page.started_at') }}: {{ process.started_at ? $moment(process.started_at).format('DD MMM YYYY, HH:mm') : '-' }}
              </span>
              <span v-if="process.status === 'ON_PROGRESS'" class="status-text ml-3">
                • {{ $t('product.import_page.processing_wait') }}
              </span>
              <span v-else-if="process.status === 'PENDING'" class="status-text ml-3">
                • {{ $t('product.import_page.waiting_resources') }}
              </span>
            </div>
            <div class="footer-right">
              <div v-if="(process.status !== 'FAILED' || sessionRequestIds.includes(process.request_id)) && (process.items_total || 0) > 0" class="progress-stats-v2">
                {{ Math.round((( (process.items_completed || 0) + (process.items_failed || 0) + (process.items_skipped || 0) ) / (process.items_total || 1)) * 100) }}% - 
                {{ (process.items_completed || 0) + (process.items_failed || 0) + (process.items_skipped || 0) }} {{ $t('product.import_page.of') }} {{ process.items_total }} {{ $t('product.import_page.skus_processed') }}
              </div>
            </div>
          </div>
        </div>
      </a-card>

      <div v-if="loadingHistory || historyProcesses.length > 0" id="process-history-section" class="mt-4">
        <a-divider v-if="activeProcesses.length > 0" class="mb-4" style="width: 75%" />
        <h6 v-if="activeProcesses.length > 0" style="font-weight: bold" class="mb-3">
          {{ $t('product.import_page.process_history_title') }}
        </h6>
        <a-table
          :columns="historyColumns"
          :data-source="historyProcesses"
          :row-key="record => record.request_id"
          size="middle"
          class="history-table custom-table"
          :pagination="false"
          :loading="loadingHistory"
        >
          <template slot="file_name" slot-scope="text, record">
            <div class="file-name-cell">
              <span class="file-name-text">{{ text }}</span>
              <span v-if="record.user_name" class="uploaded-by-text">
                <a-icon type="user" style="font-size: 10px; margin-right: 2px;" />{{ record.user_name }}
              </span>
            </div>
          </template>
          <span slot="started_at" slot-scope="text">
            {{ text ? $moment(text).format('DD MMM YYYY, HH:mm') : '-' }}
          </span>
          <span slot="sku_success" slot-scope="text, record">
            <span class="text-success" style="font-weight: bold">{{ record.items_completed }}</span>
          </span>
          <span slot="sku_failed" slot-scope="text, record">
            <span class="text-danger" style="font-weight: bold">{{ record.items_failed }}</span>
          </span>
          <span slot="sku_skipped" slot-scope="text, record">
            <span class="text-warning" style="font-weight: bold">{{ record.items_skipped }}</span>
          </span>
          <span slot="action" slot-scope="text, record">
            <a-button type="link" size="small" @click="viewDetail(record)">
              {{ $t('product.import_page.view_details') }}
            </a-button>
          </span>
        </a-table>

        <Pagination
          v-if="historyPagination.total_elements > historyLimit"
          class="mt-3 text-right"
          style="width: 75%"
          :total="historyPagination.total_elements"
          :page-size="historyLimit"
          :total-length-per-page="historyProcesses.length"
          :default-page="historyPage"
          @changePage="handleHistoryPageChange"
        />
        <div class="mt-2 text-right" style="width: 75%">
          <small class="text-muted"><a-icon type="info-circle" /> <span v-html="$t('product.import_page.reupload_tip')" /></small>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <a-modal
      v-model="showDetailModal"
      :width="900"
      :footer="null"
      :top="20"
      :body-style="{ padding: '0px' }"
      :closable="false"
    >
      <div v-if="selectedProcess">
        <div class="modal-header-premium">
          <div class="header-left">
            <h5 class="mb-0">
              {{ $t('product.import_page.process_details_title') }}
            </h5>
            <small class="text-muted">{{ selectedProcess.file_name }}</small>
          </div>
          <div class="header-right">
            <a-tag :color="getStatusColor(selectedProcess.status)" class="status-capsule">
              {{ selectedProcess.status }}
            </a-tag>
            <a-button type="link" class="close-modal-btn" @click="showDetailModal = false">
              <a-icon type="close" />
            </a-button>
          </div>
        </div>

        <div class="modal-body-premium">
          <a-card class="detail-info-card" :bordered="false" size="small">
            <a-row :gutter="24">
              <a-col :span="12">
                <div class="info-item">
                  <span class="label">{{ $t('product.import_page.uploaded_by_label') }}</span>
                  <div class="value">
                    <a-icon type="user" style="font-size: 12px; margin-right: 4px;" />{{ selectedProcess.user_name || '-' }}
                  </div>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="info-item">
                  <span class="label">{{ $t('product.import_page.started_at') }}</span>
                  <div class="value">
                    {{ selectedProcess.started_at ? $moment(selectedProcess.started_at).format('DD MMM YYYY, HH:mm') : '-' }}
                  </div>
                </div>
              </a-col>
            </a-row>
          </a-card>
          
          <div class="stats-container-compact">
            <div 
              class="stat-box info" 
              :class="{ active: selectedStatusFilter === 'ALL' }"
              @click="setStatusFilter('ALL')"
            >
              <span class="label">{{ $t('product.import_page.total_label') }}</span>
              <span class="value">{{ selectedProcess.items_total }}</span>
              <a-icon v-if="selectedStatusFilter === 'ALL'" type="check-circle" class="active-icon" />
            </div>
            <div 
              class="stat-box success"
              :class="{ active: selectedStatusFilter === 'SUCCESS' }"
              @click="setStatusFilter('SUCCESS')"
            >
              <span class="label">{{ $t('product.import_page.success_label') }}</span>
              <span class="value">{{ selectedProcess.items_completed }}</span>
              <a-icon v-if="selectedStatusFilter === 'SUCCESS'" type="check-circle" class="active-icon" />
            </div>
            <div 
              class="stat-box danger"
              :class="{ active: selectedStatusFilter === 'FAILED' }"
              @click="setStatusFilter('FAILED')"
            >
              <span class="label">{{ $t('product.import_page.failed_label') }}</span>
              <span class="value">{{ selectedProcess.items_failed }}</span>
              <a-icon v-if="selectedStatusFilter === 'FAILED'" type="check-circle" class="active-icon" />
            </div>
            <div 
              class="stat-box warning"
              :class="{ active: selectedStatusFilter === 'SKIPPED' }"
              @click="setStatusFilter('SKIPPED')"
            >
              <span class="label">{{ $t('product.import_page.skipped_label') }}</span>
              <span class="value">{{ selectedProcess.items_skipped || 0 }}</span>
              <a-icon v-if="selectedStatusFilter === 'SKIPPED'" type="check-circle" class="active-icon" />
            </div>
          </div>

          <div class="table-wrapper-detail">
            <a-table
              :columns="detailColumns"
              :data-source="processItems"
              :row-key="(record, index) => index"
              size="middle"
              :loading="loadingDetail"
              :pagination="false"
              class="premium-detail-table"
            >
              <span slot="sku" slot-scope="text" class="sku-mono">
                {{ (text || '').toUpperCase() }}
              </span>
              <span slot="item_status" slot-scope="text">
                <a-tag :color="getStatusColor(text)" class="status-capsule mini">
                  {{ text }}
                </a-tag>
              </span>
            </a-table>
          </div>

          <Pagination
            class="mt-4 text-right"
            :total="detailPagination.total_elements"
            :page-size="detailLimit"
            :total-length-per-page="processItems.length"
            :default-page="detailPage"
            @changePage="handleDetailPageChange"
          />
        </div>
      </div>
    </a-modal>

    <a-card v-if="isUpload" class="mt-4" style="width: 75%">
      <h6 class="mb-3" style="font-weight: bold">
        {{ $t('product.import_page.import_data_title') }}
      </h6>
      <template v-if="['non-variant', 'multi-variant'].includes(isVariant)">
        <p v-if="!result.length && !isFailed && isUpload" class="mb-0">
          <a-icon type="check-circle" theme="filled" class="mr-2" style="color: green" />
          {{ $t('product.import_page.success_message') }}
          <router-link
            :to="{
              path: '/product',
              query: {
                workspace_id: $route.query.workspace_id,
                business_id: $route.query.business_id,
                warehouse_id: $route.query.warehouse_id,
              },
            }"
          >
            <b>{{ $t('product.listProduct') }}</b>
          </router-link>
        </p>
        <p v-if="!result.length && isFailed && isUpload" class="mb-0">
          <a-icon type="warning" theme="filled" class="mr-2" style="color: red" />
          {{ $t('product.import_page.failed_all_message') }}
        </p>
        <p v-if="result.length && !isFailed && isUpload">
          <a-icon type="warning" theme="filled" class="mr-2" style="color: red" />
          {{ $t('product.import_page.failed_partial_message') }} <b style="color: red">{{ result.length }}</b> {{ $t('product.import_page.failed_data') }}
        </p>
        <a-table
          v-if="result.length && !isFailed && isUpload"
          :columns="columns"
          :row-key="(record, index) => index"
          :data-source="result"
          :pagination="false"
          :loading="uploadLoading"
        />
      </template>
      <template v-else-if="['create-bundling'].includes(isVariant)">
        <p v-if="!(resultMaster.length || resultBundling.length) && !isFailed && isUpload" class="mb-0">
          <a-icon type="check-circle" theme="filled" class="mr-2" style="color: green" />
          {{ $t('product.import_page.success_message') }}
          <router-link
            :to="{
              path: '/product',
              query: {
                workspace_id: $route.query.workspace_id,
                business_id: $route.query.business_id,
                warehouse_id: $route.query.warehouse_id,
              },
            }"
          >
            <b>{{ $t('product.listProduct') }}</b>
          </router-link>
        </p>
        <p v-if="!(resultMaster.length || resultBundling.length) && isFailed && isUpload" class="mb-0">
          <a-icon type="warning" theme="filled" class="mr-2" style="color: red" />
          {{ $t('product.import_page.failed_all_message') }}
          <span v-if="!(resultFileWithoutRowNumber.length) && isFailed && isUpload" class="mb-0">
            {{ resultFileWithoutRowNumber.map(item => item.note).join(',') }}
          </span>
        </p>
        <p v-if="resultFileWithoutRowNumber.length && isFailed && isUpload" class="mb-0">
          <a-icon type="warning" theme="filled" class="mr-2" style="color: red" />
          {{ $t('product.import_page.failed_all_message') }}
          <i class="mb-0" style="color: red">
            {{ resultFileWithoutRowNumber.map(item => item.note).join(',') }}
          </i>
        </p>
        <p v-if="(resultMaster.length || resultBundling.length) && isFailed && isUpload" class="mb-0">
          <a-icon type="warning" theme="filled" class="mr-2" style="color: red" />
          {{ $t('product.import_page.failed_partial_message') }}
          <span v-if="resultMaster.length > 0"><b style="color: red"> {{ resultMaster.length }} {{ $t('product.import_page.failed_master_data') }}</b> yang gagal.</span>
          <span v-if="resultBundling.length > 0"><b style="color: red"> {{ resultBundling.length }} {{ $t('product.import_page.failed_bundling_data') }}</b> yang gagal.</span>
        </p>
        <template v-if="resultMaster.length && isFailed && isUpload">
          <a-divider orientation="left">
            {{ $t('product.import_page.data_master') }}
          </a-divider>
          <a-table
            :columns="columns"
            :row-key="(record, index) => index"
            :data-source="resultMaster"
            :pagination="false"
            :loading="uploadLoading"
          />
        </template>
        <template v-if="resultBundling.length && isFailed && isUpload">
          <a-divider orientation="left">
            {{ $t('product.import_page.data_bundling') }}
          </a-divider>
          <a-table
            v-if="resultBundling.length && isFailed && isUpload"
            :columns="columns"
            :row-key="(record, index) => index"
            :data-source="resultBundling"
            :pagination="false"
            :loading="uploadLoading"
          />
        </template>
      </template>
    </a-card>
  </a-card>
</template>

<script>
import Pagination from '@/components/Pagination'
import {
  downloadFile,
  uploadFile,
  downloadTemplateCreateBundling,
  uploadFileCreateBundling,
  checkRequestStatusUploadFileCreateBundling,
  getAllProcessesUploadFileCreateBundling,
  getActiveProcessesUploadFileCreateBundling,
  cancelProcessUploadFileCreateBundling,
} from '@/api/product'

export default {
  name: 'ImportProductPage',
  components: {
    Pagination,
  },
  data() {
    return {
      downloadLoading: false,
      uploadLoading: false,
      isUpload: false,
      isFailed: false,
      isVariant: 'non-variant',
      fileList: [],
      result: [],
      resultMaster: [],
      resultBundling: [],
      resultFileWithoutRowNumber: [],
      resUploadCreateBundling: [],
      allProcesses: [],
      showDetailModal: false,
      selectedProcess: null,
      processItems: [],
      loadingDetail: false,
      detailPage: 1,
      detailLimit: 10,
      selectedStatusFilter: 'ALL',
      sessionRequestIds: [], // Track request IDs from this session for ephemeral visibility
      loadingHistory: false, // Loading state for history table
      pollingTimeout: null, // For recursive setTimeout
      historyPage: 1,
      historyLimit: 5,
      historyPagination: { total_elements: 0, total_pages: 0 }, // Backend pagination info
      detailPagination: { total_elements: 0, total_pages: 0 }, // Backend pagination info for details
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('product.import_page.row'),
          dataIndex: 'row',
          width: '20%',
        },
        {
          title: this.$t('product.import_page.note'),
          dataIndex: 'note',
          width: '80%',
        },
      ]
    },
    historyColumns() {
      return [
        {
          title: this.$t('product.import_page.file_name_label') || 'File Name',
          dataIndex: 'file_name',
          key: 'file_name',
          scopedSlots: { customRender: 'file_name' },
        },
        {
          title: this.$t('product.import_page.total_label') || 'Total',
          dataIndex: 'items_total',
          key: 'items_total',
          align: 'center',
          width: 80,
        },
        {
          title: this.$t('product.import_page.success_label') || 'Success',
          key: 'sku_success',
          align: 'center',
          scopedSlots: { customRender: 'sku_success' },
          width: 80,
        },
        {
          title: this.$t('product.import_page.failed_label') || 'Failed',
          key: 'sku_failed',
          align: 'center',
          scopedSlots: { customRender: 'sku_failed' },
          width: 80,
        },
        {
          title: this.$t('product.import_page.skipped_label') || 'Skipped',
          key: 'sku_skipped',
          align: 'center',
          scopedSlots: { customRender: 'sku_skipped' },
          width: 80,
        },
        {
          title: this.$t('product.import_page.started_at_label') || 'Started At',
          dataIndex: 'started_at',
          key: 'started_at',
          scopedSlots: { customRender: 'started_at' },
          width: 160,
        },
        {
          title: this.$t('product.import_page.action_label') || 'Action',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 120,
        },
      ]
    },
    detailColumns() {
      return [
        { 
          title: this.$t('product.import_page.sku_label') || 'SKU', 
          dataIndex: 'sku', 
          key: 'sku', 
          scopedSlots: { customRender: 'sku' }, 
        },
        { title: this.$t('product.import_page.status_label') || 'Status', dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'item_status' }, width: 110, align: 'center' },
        { title: this.$t('product.import_page.message_label') || 'Message', dataIndex: 'message', key: 'message' },
      ]
    },
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    currency() {
      return this.$store.getters['user/businessListObjectByKey'][this.businessId]?.currency?.currency_code || 'IDR'
    },
    userId() {
      // Use id as primary, fallback to email or uuid or key
      return this.$store.state.user.id || 
             this.$store.state.user.email || 
             this.$store.state.user.uuid || 
             this.$store.state.user.key || 
             'guest'
    },
    trackedIdsKey() {
      return `bulk_bundling_tracked_ids_${this.businessId}_${this.userId}`
    },
    activeProcesses() {
      // Global: ON_PROGRESS or PENDING
      // Local: Any requestId initiated in this session (ephemeral)
      return this.allProcesses.filter(p => 
        ['ON_PROGRESS', 'PENDING'].includes(p.status) || 
        this.sessionRequestIds.includes(p.request_id),
      )
    },
    historyProcesses() {
      // Backend already returns only completed processes (not ON_PROGRESS/PENDING) with pagination
      return this.allProcesses
    },
    hasCompletedSessionProcesses() {
      // Check if there are any session processes that have completed (FINISHED or FAILED)
      // but are still shown in active view (because they're in sessionRequestIds)
      return this.sessionRequestIds.some(sid => {
        const process = this.allProcesses.find(p => p.request_id === sid)
        return process && ['FINISHED', 'FAILED', 'CANCELLED'].includes(process.status)
      })
    },
    filteredDetailItems() {
      // Obsolete now that we use server-side pagination
      return this.processItems
    },
  },
  async mounted() {
    // Only fetch bundling processes if we start on that tab (unlikely but possible via URL params)
    if (this.isVariant === 'create-bundling') {
      // Load tracked IDs from localStorage into memory
      this.sessionRequestIds = this.getTrackedIds()
      
      await this.fetchAllProcesses()
      if (this.activeProcesses.length > 0) {
        this.startPolling()
      }
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async fetchAllProcesses(showLoading = true) {
      if (!this.businessId) return
      if (showLoading) this.loadingHistory = true
      
      try {
        const { data: response } = await getAllProcessesUploadFileCreateBundling({
          process_name: 'BULK_CREATE_BUNDLING',
          business_id: this.businessId,
          page: this.historyPage - 1, // Backend uses 0-indexed
          size: this.historyLimit,
        })
        
        // New paginated response structure: { data: [...], current_page, total_pages, total_elements, size }
        this.allProcesses = response.data || []
        this.historyPagination = {
          total_elements: response.total_elements || 0,
          total_pages: response.total_pages || 0,
        }
        
        // Auto-start polling if there are active processes
        const hasActive = this.allProcesses.some(p => ['ON_PROGRESS', 'PENDING'].includes(p.status))
        if (hasActive) {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      } catch (err) {
        console.error('[Product Import] Error fetching historical processes:', err)
      } finally {
        if (showLoading) this.loadingHistory = false
      }
    },
    startPolling(interval = 3000) {
      if (this.pollingTimeout) return
      
      const poll = async () => {
        await this.fetchActiveProcesses()
        if (this.pollingTimeout !== null) {
          this.pollingTimeout = setTimeout(poll, interval)
        }
      }
      
      this.pollingTimeout = setTimeout(poll, interval)
    },
    stopPolling() {
      if (this.pollingTimeout) {
        clearTimeout(this.pollingTimeout)
        this.pollingTimeout = null
      }
    },
    async fetchActiveProcesses() {
      if (!this.businessId) return
      
      try {
        const { data: activeData } = await getActiveProcessesUploadFileCreateBundling({
          process_name: 'BULK_CREATE_BUNDLING',
          business_id: this.businessId,
        })
        
        const activeProcesses = activeData || []
        const activeIds = activeProcesses.map(p => p.request_id)
        
        // Auto-adopt any globally active IDs into our local tracking list
        activeIds.forEach(id => this.addTrackedId(id))

        // Update progress for processes in memory
        if (activeProcesses.length > 0) {
          activeProcesses.forEach(activeProcess => {
            const index = this.allProcesses.findIndex(p => p.request_id === activeProcess.request_id)
            if (index !== -1) {
              this.$set(this.allProcesses, index, { ...this.allProcesses[index], ...activeProcess })
            } else {
              this.allProcesses.unshift(activeProcess)
            }
          })
        }
        
        // Stop polling if no active processes found from server
        if (activeIds.length === 0 && this.pollingTimeout) {
          this.stopPolling()
          await this.fetchAllProcesses(false)
        }

        // Cleanup localStorage: Remove finished IDs
        this.allProcesses.forEach(p => {
          if (['FINISHED', 'FAILED', 'CANCELLED'].includes(p.status)) {
            this.removeTrackedId(p.request_id)
          }
        })
      } catch (err) {
        console.error('[Product Import] Error polling active processes:', err)
      }
    },
    getTrackedIds() {
      try {
        const ids = localStorage.getItem(this.trackedIdsKey)
        return ids ? JSON.parse(ids) : []
      } catch (e) {
        console.error('[Product Import] Failed to read localStorage:', e)
        return []
      }
    },
    saveTrackedIds(ids) {
      try {
        localStorage.setItem(this.trackedIdsKey, JSON.stringify(ids))
      } catch (e) {
        console.error('[Product Import] Failed to write localStorage:', e)
      }
    },
    addTrackedId(id) {
      const ids = this.getTrackedIds()
      if (!ids.includes(id)) {
        ids.push(id)
        this.saveTrackedIds(ids)
      }
      // Always ensure it is in memory too
      if (!this.sessionRequestIds.includes(id)) {
        this.sessionRequestIds.push(id)
      }
    },
    removeTrackedId(id) {
      const ids = this.getTrackedIds()
      if (ids.includes(id)) {
        const newIds = ids.filter(trackedId => trackedId !== id)
        this.saveTrackedIds(newIds)
      }
    },
    async clearCompletedProcesses() {
      // Clear session tracking - this moves completed/failed processes out of active view
      // They will still be visible in history
      this.sessionRequestIds = []
      this.$message.success('Completed processes cleared from active view')
    },
    async cancelProcess(requestId) {
      console.log('Attempting to cancel process:', requestId)
      try {
        await cancelProcessUploadFileCreateBundling({
          requestId,
          params: { process_name: 'BULK_CREATE_BUNDLING' },
        })
        console.log('Cancel request successful for:', requestId)
        this.$message.success('Process cancellation requested')
        
        // Update local state instead of reloading all
        const index = this.allProcesses.findIndex(p => p.request_id === requestId)
        if (index !== -1) {
          this.$set(this.allProcesses, index, { 
            ...this.allProcesses[index], 
            status: 'CANCELLED', 
          })
        }
      } catch (err) {
        console.error('Error cancelling process:', err)
        this.$message.error('Failed to cancel process')
      }
    },
    async viewDetail(process, page = 1) {
      if (!this.showDetailModal) {
        this.selectedStatusFilter = 'ALL'
      }
      this.selectedProcess = process
      this.showDetailModal = true
      this.loadingDetail = true
      this.detailPage = page
      
      try {
        const { data: response } = await checkRequestStatusUploadFileCreateBundling({
          requestId: process.request_id,
          params: { 
            process_name: 'BULK_CREATE_BUNDLING',
            page: this.detailPage - 1,
            size: this.detailLimit,
            status: this.selectedStatusFilter !== 'ALL' ? this.selectedStatusFilter : undefined,
          },
        })
        
        // Update selectedProcess with latest counts from server
        if (response.data) {
          const { items, ...processData } = response.data
          this.selectedProcess = { ...this.selectedProcess, ...processData }
          this.processItems = items || []
        }
        
        this.detailPagination = {
          total_elements: response.total_elements || 0,
          total_pages: response.total_pages || 0,
        }
      } catch (err) {
        console.error('Error fetching process details:', err)
        this.$message.error('Failed to load details')
      } finally {
        this.loadingDetail = false
      }
    },
    async downloadTemplate() {
      this.downloadLoading = true
      if (['non-variant', 'multi-variant'].includes(this.isVariant)) {
        const isMultiVariant = this.isVariant === 'multi-variant'
        await downloadFile({
          businessId: this.businessId,
          params: {
            warehouse_ids: this.$route.query.warehouse_id,
            is_variant: isMultiVariant,
          },
        })
          .then(res => {
            if (res?.data) {
              const a = document.createElement('a')
              a.href = window.URL.createObjectURL(res.data)
              a.download = `template - ${!isMultiVariant ? 'Non Variant' : 'Multi Variant'}.xlsx`
              document.body.appendChild(a)
              a.click()
              setTimeout(() => {
                a.remove()
              }, 9)
            }
          })
          .catch(err => console.error(err))
          .finally(() => this.downloadLoading = false)
      } else if (['create-bundling'].includes(this.isVariant)) {
        await downloadTemplateCreateBundling({
          params: {
            business_id: this.businessId,
          },
        })
          .then(res => {
            if (res?.data) {
              const a = document.createElement('a')
              a.href = window.URL.createObjectURL(res.data)
              a.download = `template - create bundling.xlsx`
              document.body.appendChild(a)
              a.click()
              setTimeout(() => {
                a.remove()
              }, 9)
            }
          })
          .catch(err => console.error(err))
          .finally(() => this.downloadLoading = false)
      } else {
        this.downloadLoading = false
        this.$message.warning('This variant type does not support template download yet')
      }
    },
    async uploadTemplate() {
      this.isUpload = false
      this.uploadLoading = true
      if (['non-variant', 'multi-variant'].includes(this.isVariant)) {
        const { workspace_id } = this.$route.query
        const formData = new FormData()
        const isMultiVariant = this.isVariant === 'multi-variant'
        formData.append('file', this.fileList[0])
        formData.append('is_variant', isMultiVariant)
        formData.append('currency', this.currency)
        await uploadFile({
          workspaceId: workspace_id,
          businessId: this.businessId,
          formData,
        })
          .then(({ data: { data: response } }) => {
            this.isFailed = false
            this.fileList = []
            this.result = response?.error_data_list || []
          })
          .catch(err => {
            this.isFailed = true
            this.result = []
            console.error(err)
          })
          .finally(() => {
            this.uploadLoading = false
            this.isUpload = true
          })
      } else if (['create-bundling'].includes(this.isVariant)) {
        const { workspace_id } = this.$route.query
        const formData = new FormData()
        formData.append('file', this.fileList[0])
        formData.append('currency', this.currency)
        uploadFileCreateBundling({
          workspaceId: workspace_id,
          businessId: this.businessId,
          formData,
        })
          .then(({ data: response }) => {
            this.resUploadCreateBundling = response
            this.fileList = []
            
            // Reset session tracking (NOT allProcesses - that's backend data!)
            this.sessionRequestIds = []
            
            // Track current upload in session for ephemeral visibility
            this.sessionRequestIds.push(response.request_id)
            
            // Manually push to allProcesses to ensure immediate visibility
            const newProcess = {
              ...response,
              items_completed: 0,
              items_total: 0,
              items_failed: 0,
              items_skipped: 0,
              started_at: new Date().toISOString(),
            }
            
            const exists = this.allProcesses.some(p => p.request_id === response.request_id)
            if (!exists) {
              this.allProcesses.unshift(newProcess)
            }
            
            // Add to persistent tracking
            this.addTrackedId(response.request_id)
            
            if (response.status.toUpperCase() !== 'FAILED') {
              this.isFailed = false
              this.$message.success('Bulk bundling process started')
              this.startPolling()
            } else {
              this.isFailed = true
              this.$message.warning('File uploaded but has validation errors')
            }
          })
          .catch(err => {
            this.isFailed = true
            this.uploadLoading = false
            this.$message.error('Failed to start bulk bundling process')
            console.error(err)
          })
          .finally(() => {
            this.uploadLoading = false
          })
      } else {
        this.isUpload = false
        this.uploadLoading = false
        this.$message.warning('This variant type does not support bulk upload yet')
      }
    },
    onCheckProcessUpload(requestId) {
      // Keep for backward compatibility if needed, but not primarily used
      checkRequestStatusUploadFileCreateBundling({
        requestId: requestId,
        params: {
          process_name: 'BULK_CREATE_BUNDLING',
        },
      })
        .then(({ data: response }) => {
          if (response.status === 'ON_PROGRESS') {
            setTimeout(() => {
              this.onCheckProcessUpload(response.request_id)
            }, 2000)
          } else if (response.status === 'FINISHED') {
            this.uploadLoading = false
            this.isUpload = true
          }
        })
        .catch(err => console.error(err))
    },
    getStatusColor(status) {
      switch (status) {
        case 'FINISHED':
        case 'COMPLETED':
        case 'SUCCESS': return 'green'
        case 'ON_PROGRESS': return 'blue'
        case 'FAILED': return 'red'
        case 'SKIPPED': return 'orange'
        case 'CANCELLED': return 'orange'
        default: return 'gray'
      }
    },
    getStatusBadgeType(status) {
      switch (status) {
        case 'FINISHED':
        case 'COMPLETED':
        case 'SUCCESS': return 'success'
        case 'ON_PROGRESS': return 'processing'
        case 'FAILED': return 'error'
        case 'CANCELLED': return 'warning'
        default: return 'default'
      }
    },
    scrollToHistory() {
      const element = document.getElementById('process-history-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    handleHistoryPageChange(page) {
      this.historyPage = page
      this.fetchAllProcesses()
    },
    handleDetailPageChange(page) {
      this.viewDetail(this.selectedProcess, page)
    },
    setStatusFilter(status) {
      this.selectedStatusFilter = status
      this.viewDetail(this.selectedProcess, 1)
    },
    async changeVariant() {
      this.resetResult()
      this.fileList = []
      this.historyPage = 1
      
      // Fetch bundling history only when switching to that tab
      if (this.isVariant === 'create-bundling') {
        // Load tracked IDs from localStorage
        this.sessionRequestIds = this.getTrackedIds()
        
        await this.fetchAllProcesses()
        if (this.activeProcesses.length > 0) {
          this.startPolling()
        }
      } else {
        // Stop polling and clear bundling data when leaving the tab
        this.stopPolling()
        this.allProcesses = []
        this.sessionRequestIds = []
      }
    },
    handleRemove(file) {
      const index = this.fileList.indexOf(file)
      const newFileList = this.fileList.slice()
      newFileList.splice(index, 1)
      this.fileList = newFileList
    },
    beforeUpload(file) {
      this.resetResult()
      this.fileList = [...this.fileList, file]
      return false
    },
    resetResult() {
      this.isUpload = false
      this.result = []
    },
  },
}
</script>

<style lang="scss">
.product-variant {
  .ant-radio-button-wrapper {
    margin-right: 15px;
    border: 1px solid #e4e9f0;
    border-radius: 25px !important;
    outline: none !important;
    box-shadow: none !important;

    &::before {
      content: none !important;
    }
  }
}

.active-process-card-v2 {
  width: 75%;
  border-radius: 8px !important;
  border: 1px solid #eef0f2;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
  margin-bottom: 20px;
  
  .process-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;

      .file-name {
        font-weight: 600;
        font-size: 16px;
        color: #1a1a1a;
      }

      .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 500;
        background: #f0f2f5;
        color: #595959;

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #bfbfbf;
        }

        &.on_progress {
          background: #e6f7ff;
          color: #1890ff;
          .dot { 
            background: #1890ff; 
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
            animation: pulse-dot 1.5s infinite;
          }
        }

        &.failed {
          background: #fff1f0;
          color: #f5222d;
          .dot { background: #f5222d; }
        }

        &.pending {
          background: #fffbe6;
          color: #faad14;
          .dot { background: #faad14; }
        }
      }
    }

    .header-right {
      .detail-btn {
        border-radius: 20px;
        height: 32px;
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
      }
      .cancel-btn {
        border-radius: 20px;
        padding: 0 16px;
        color: #ffffff;
        font-size: 13px;
        background: #E00000;
        border: none;
        height: 32px;
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(224, 0, 0, 0.2);

        &:hover {
          color: #ffffff;
          background: #f83a3a;
          box-shadow: 0 4px 8px rgba(224, 0, 0, 0.3);
        }
      }
    }
  }

  .process-body {
    .progress-bar-wrapper {
      height: 24px;
      background: #f0f2f5;
      border-radius: 12px;
      overflow: hidden;
      position: relative;
      margin-bottom: 8px;

      .custom-progress-bar {
        height: 100%;
        background: linear-gradient(90deg, #1890ff 0%, #07beb8 100%);
        border-radius: 12px;
        transition: width 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-right: 8px;
        min-width: 40px;

        &.failed {
          background: #f5222d !important;
        }

        .inner-percentage {
          color: white;
          font-size: 11px;
          font-weight: 700;
        }
      }
    }

    .validation-error-banner {
      background: #fff1f0;
      border: 1px solid #ffa39e;
      padding: 10px 16px;
      border-radius: 6px;
      display: flex;
      align-items: flex-start;
      
      .anticon {
        color: #f5222d;
        font-size: 16px;
      }
      
      .error-text {
        color: #f5222d;
        font-weight: 500;
        font-size: 13px;
        line-height: 1.4;
      }
    }

    .validation-loading-banner {
      background: #e6f7ff;
      border: 1px solid #91d5ff;
      padding: 12px 16px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      
      .loading-text {
        color: #1890ff;
        font-weight: 500;
        font-size: 13px;
      }
    }

    .session-result-summary {
      display: flex;
      gap: 20px;
      padding: 12px 16px;
      background: #fafafa;
      border-radius: 6px;
      border: 1px dashed #d9d9d9;

      .summary-item {
        display: flex;
        align-items: center;
        gap: 6px;
        
        .dot { width: 6px; height: 6px; border-radius: 50%; }
        .label { font-size: 12px; color: #595959; font-weight: 500; }
        .value { font-size: 14px; font-weight: 700; }

        &.success { .dot { background: #52c41a; } .value { color: #52c41a; } }
        &.failed { .dot { background: #f5222d; } .value { color: #f5222d; } }
        &.skipped { .dot { background: #fa8c16; } .value { color: #fa8c16; } }
      }
    }

    .process-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .footer-left {
        display: flex;
        align-items: center;
      }

      .started-at, .status-text, .progress-stats-v2 {
        font-size: 12px;
        color: #8c8c8c;
      }

      .status-text, .progress-stats-v2 {
        font-weight: 500;
      }
    }
  }
}

@keyframes pulse-dot {
  0% { transform: scale(0.95); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(0.95); opacity: 1; }
}

.history-table {
  width: 75%;
  
  .ant-table-thead > tr > th {
    background: #f8f9fb;
    font-weight: 700;
    color: #595959;
    border-bottom: 2px solid #f0f0f0;
  }

  .ant-table-row:hover {
    td {
      background-color: #fbfcfe !important;
    }
  }
}

.custom-table {
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}

.status-capsule {
  border-radius: 20px;
  padding: 0 12px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
}

.flex { display: flex; }
.flex-1 { flex: 1; }
.justify-between { justify-content: space-between; }
.items-center { align-items: center; }
.text-right { text-align: right; }
.text-success { color: #52c41a; }
.text-danger { color: #f5222d; }
.text-warning { color: #faad14; }
.text-muted { color: #8c8c8c; }
.font-bold { font-weight: bold; }
.mr-1 { margin-right: 4px; }
.gap-4 { gap: 16px; }

.stats-mini-card {
  text-align: center;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  
  .label { font-size: 11px; color: #8c8c8c; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
  .value { font-size: 24px; font-weight: 800; }
  
  &.success { border-bottom: 4px solid #52c41a; .value { color: #52c41a; } }
  &.danger { border-bottom: 4px solid #f5222d; .value { color: #f5222d; } }
  &.warning { border-bottom: 4px solid #faad14; .value { color: #faad14; } }
  &.info { border-bottom: 4px solid #1890ff; .value { color: #1890ff; } }
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}
.pulse {
  animation: pulse 2.5s infinite ease-in-out;
}
.modal-header-premium {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #fdfdfd;
  border-bottom: 1px solid #f0f0f0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  .header-left {
    h5 { font-weight: 700; color: #262626; margin: 0; font-size: 16px; }
    small { font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace; color: #bfbfbf; font-size: 11px; }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;

    .close-modal-btn {
      color: #bfbfbf;
      font-size: 18px;
      padding: 0;
      height: auto;
      &:hover { color: #595959; }
    }
  }
}

.modal-body-premium {
  padding: 16px 24px;
}

.detail-info-card {
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 12px;
  .info-item {
    .label { font-size: 11px; color: #8c8c8c; display: block; margin-bottom: 2px; }
    .value { font-size: 13px; font-weight: 600; color: #262626; }
  }
}

.stats-container-compact {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;

  .stat-box {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 2px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    
    .label { font-size: 11px; color: #8c8c8c; text-transform: uppercase; font-weight: 500; }
    .value { font-size: 16px; font-weight: 700; }
    .active-icon { position: absolute; top: -6px; right: -6px; font-size: 16px; border-radius: 50%; background: white; z-index: 2; }

    &:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }

    &.active {
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      z-index: 1;
    }

    &.success { 
      background: #f6ffed; border-color: #f0f0f0; .value { color: #52c41a; }
      &.active { border-color: #52c41a; .active-icon { color: #52c41a; } }
    }
    &.danger { 
      background: #fff1f0; border-color: #f0f0f0; .value { color: #f5222d; }
      &.active { border-color: #f5222d; .active-icon { color: #f5222d; } }
    }
    &.warning { 
      background: #fff7e6; border-color: #f0f0f0; .value { color: #fa8c16; }
      &.active { border-color: #fa8c16; .active-icon { color: #fa8c16; } }
    }
    &.info { 
      background: #e6f7ff; border-color: #f0f0f0; .value { color: #1890ff; }
      &.active { border-color: #1890ff; .active-icon { color: #1890ff; } }
    }
  }
}

.table-wrapper-detail {
  border: 1px solid #e4e9f0;
  border-radius: 5px;
  overflow: hidden;
}

.premium-detail-table {
  ::v-deep(.ant-table-thead > tr > th) {
    background: #fafafa;
    font-weight: 700;
  }
}

.sku-mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-weight: 600;
  color: #1890ff;
  background: rgba(24, 144, 255, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
}

.status-capsule.mini {
  font-size: 9px;
  padding: 0 8px;
  height: 20px;
  line-height: 18px;
}

// File name cell with user_name subtitle
.file-name-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .file-name-text {
    font-weight: 500;
    color: #1a1a1a;
  }

  .uploaded-by-text {
    font-size: 11px;
    color: #8c8c8c;
    display: inline-flex;
    align-items: center;
  }
}
</style>